// Hold workflow panel for a ticket: request (technician), approve/reject (NOC), release,
// plus current status + history with evidence preview. Self-contained; used in TicketDetail.
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { PauseCircle, PlayCircle, Check, X, ShieldQuestion } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import { HOLD_REASONS, HOLD_STATUS_LABEL, humanSeconds, fmtLocal } from '../helpdeskUtils';
import UploadZone from './UploadZone';
import { FileImage } from './FileImage';
import { cn } from '@/lib/utils';

export default function HoldPanel({ ticket, user, canApprove, onChanged, onPreview }) {
  const [openReq, setOpenReq] = useState(false);
  const [openReject, setOpenReject] = useState(false);
  const [reason, setReason] = useState(HOLD_REASONS[0]);
  const [note, setNote] = useState('');
  const [fileIds, setFileIds] = useState([]);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);

  const id = ticket.id;
  const holdStatus = ticket.hold_status;
  const cur = ticket.hold_current;
  const isTroubleshooter = user?.id && ticket.troubleshooter_id === user.id;
  const canRelease = canApprove || isTroubleshooter;
  const evidenceFiles = (ticket.files || []).filter((f) => f.evidence_type === 'HOLD_EVIDENCE');

  const submitRequest = async () => {
    if (!reason) return toast.error('Pilih alasan hold');
    if (!fileIds.length) return toast.error('Upload bukti konfirmasi dulu');
    setBusy(true);
    try {
      await api.post(`/crm/tickets/${id}/hold/request`, { reason, note, evidence_file_ids: fileIds });
      toast.success('Pengajuan hold dikirim — menunggu approval NOC');
      setOpenReq(false); setNote(''); setFileIds([]); onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const doAction = async (path, body, okMsg, closeReject) => {
    setBusy(true);
    try {
      await api.post(`/crm/tickets/${id}/hold/${path}`, body || {});
      toast.success(okMsg);
      if (closeReject) { setOpenReject(false); setRejectReason(''); }
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const evPreview = (fid) => {
    const f = evidenceFiles.find((x) => x.id === fid) || (ticket.files || []).find((x) => x.id === fid);
    return f;
  };

  return (
    <div className="rounded-lg border border-border bg-card/60 p-4 space-y-3" data-testid="hold-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <PauseCircle className="w-4 h-4 text-amber-500" />
          <h3 className="text-sm font-semibold">Manajemen Hold Tiket</h3>
        </div>
        <div className="flex items-center gap-2">
          {/* Request Hold — only when actively DIPROSES and no hold in progress */}
          {ticket.status === 'DIPROSES' && !holdStatus && (
            <Button size="sm" variant="outline" onClick={() => setOpenReq(true)} data-testid="hold-request-btn">
              <PauseCircle className="w-4 h-4 mr-1.5" /> Request Hold
            </Button>
          )}
          {/* Approve / Reject — NOC when PENDING */}
          {holdStatus === 'PENDING' && canApprove && (
            <>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy} onClick={() => doAction('approve', null, 'Hold di-approve — tiket ON HOLD')} data-testid="hold-approve-btn">
                <Check className="w-4 h-4 mr-1.5" /> Approve
              </Button>
              <Button size="sm" variant="outline" className="text-rose-600 border-rose-300" onClick={() => setOpenReject(true)} data-testid="hold-reject-btn">
                <X className="w-4 h-4 mr-1.5" /> Reject
              </Button>
            </>
          )}
          {/* Release — when ON_HOLD */}
          {holdStatus === 'ON_HOLD' && canRelease && (
            <Button size="sm" className="bg-sky-600 hover:bg-sky-700 text-white" disabled={busy} onClick={() => doAction('release', null, 'Hold dilepas — tiket dilanjutkan (DIPROSES)')} data-testid="hold-release-btn">
              <PlayCircle className="w-4 h-4 mr-1.5" /> Lepas Hold & Lanjutkan
            </Button>
          )}
        </div>
      </div>

      {/* Current hold state */}
      {holdStatus && cur ? (
        <div className={cn('rounded-md border p-3 text-sm space-y-2',
          holdStatus === 'PENDING' ? 'border-amber-500/40 bg-amber-500/5' : 'border-orange-500/40 bg-orange-500/5')} data-testid="hold-current">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wide">{HOLD_STATUS_LABEL[holdStatus]}</span>
            {holdStatus === 'PENDING' && <span className="text-xs text-muted-foreground">— menunggu approval NOC (timer dibekukan)</span>}
            {holdStatus === 'ON_HOLD' && <span className="text-xs text-muted-foreground">— SLA/Downtime paused sejak pengajuan</span>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <div><span className="text-muted-foreground">Alasan: </span><span className="font-medium">{cur.reason}</span></div>
            <div><span className="text-muted-foreground">Diajukan oleh: </span>{cur.requested_by_name} · {fmtLocal(cur.requested_at)}</div>
            {cur.note && <div className="sm:col-span-2"><span className="text-muted-foreground">Catatan: </span>{cur.note}</div>}
            {cur.approved_by_name && <div><span className="text-muted-foreground">Approved: </span>{cur.approved_by_name} · {fmtLocal(cur.approved_at)}</div>}
          </div>
          {/* Evidence preview */}
          {(cur.evidence_file_ids || []).length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1" data-testid="hold-evidence-preview">
              {cur.evidence_file_ids.map((fid) => {
                const f = evPreview(fid);
                if (!f) return null;
                return <FileImage key={fid} ticketId={id} file={f} onClick={() => onPreview?.(evidenceFiles, evidenceFiles.findIndex((x) => x.id === fid))} className="w-16 h-16" />;
              })}
            </div>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Tidak ada hold aktif. {ticket.hold_total_seconds ? `Total waktu hold tercatat: ${humanSeconds(ticket.hold_total_seconds)} (dikecualikan dari SLA).` : ''}</p>
      )}

      {/* History */}
      {(ticket.hold_history || []).length > 0 && (
        <div className="pt-2 border-t border-border">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1.5 font-semibold">Riwayat Hold</div>
          <ul className="space-y-1.5" data-testid="hold-history">
            {ticket.hold_history.map((h) => (
              <li key={h.id} className="text-xs flex flex-wrap items-center gap-x-2 gap-y-0.5 border-l-2 pl-2 border-border">
                <span className={cn('px-1.5 py-0.5 rounded font-semibold',
                  h.state === 'REJECTED' ? 'bg-rose-500/10 text-rose-600' :
                  h.state === 'RELEASED' ? 'bg-emerald-500/10 text-emerald-600' :
                  h.state === 'ON_HOLD' ? 'bg-orange-500/10 text-orange-600' : 'bg-amber-500/10 text-amber-600')}>{h.state}</span>
                <span className="font-medium">{h.reason}</span>
                <span className="text-muted-foreground">· {h.requested_by_name} · {fmtLocal(h.requested_at)}</span>
                {h.duration_seconds != null && <span className="text-muted-foreground">· durasi {humanSeconds(h.duration_seconds)}</span>}
                {h.reject_reason && <span className="text-rose-500">· ditolak: {h.reject_reason}</span>}
                {h.released_by_name && h.state === 'RELEASED' && <span className="text-muted-foreground">· dilepas oleh {h.released_by_name}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Request modal */}
      <Dialog open={openReq} onOpenChange={setOpenReq}>
        <DialogContent className="max-w-lg" data-testid="hold-request-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PauseCircle className="w-5 h-5 text-amber-500" /> Pengajuan Hold Tiket</DialogTitle>
            <DialogDescription>Ajukan hold jika penanganan harus ditunda. Timer SLA akan dibekukan setelah di-approve NOC.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Alasan Hold *</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="hold-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>{HOLD_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Catatan Tambahan</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Detail konteks hold…" data-testid="hold-note-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Bukti Konfirmasi * (chat/konfirmasi ke pelanggan)</Label>
              <UploadZone
                ticketId={id}
                evidenceType="HOLD_EVIDENCE"
                testKey="hold-evidence"
                onUploaded={(items) => { setFileIds((prev) => [...prev, ...items.map((i) => i.id)]); toast.success(`${items.length} bukti terunggah`); }}
              />
              {fileIds.length > 0 && <p className="text-xs text-emerald-600" data-testid="hold-evidence-count">{fileIds.length} file bukti siap dilampirkan</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenReq(false)}>Batal</Button>
            <Button onClick={submitRequest} disabled={busy || !fileIds.length} data-testid="hold-submit-btn">{busy ? 'Mengirim…' : 'Ajukan Hold'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject modal */}
      <Dialog open={openReject} onOpenChange={setOpenReject}>
        <DialogContent className="max-w-md" data-testid="hold-reject-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldQuestion className="w-5 h-5 text-rose-500" /> Tolak Pengajuan Hold</DialogTitle>
            <DialogDescription>Tiket akan kembali ke status DIPROSES dan timer SLA berlanjut.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-1">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Alasan Penolakan *</Label>
            <Textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Jelaskan alasan penolakan…" data-testid="hold-reject-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenReject(false)}>Batal</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={busy || rejectReason.trim().length < 3}
              onClick={() => doAction('reject', { reject_reason: rejectReason.trim() }, 'Pengajuan hold ditolak', true)} data-testid="hold-reject-submit">
              Tolak Hold
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
